import { Close } from '@mui/icons-material'
import SearchIcon from '@mui/icons-material/Search'
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
  OutlinedInput,
  TextField,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Controller } from 'react-hook-form'

type SelectAsyncOption = {
  value: string | number
  label: string | number
}

type SelectAsyncLoadParams = {
  search: string
  page: number
  limit: number
}

type SelectAsyncLoadResult = {
  options: Array<SelectAsyncOption>
  hasMore?: boolean
}

type SmartSelectProps = {
  control?: any
  register?: any
  name: string
  options?: { [key: string]: string | number }
  label?: string
  sx?: object
  disabled?: boolean
  topRow?: { value: string; label: string }
  searchable?: boolean
  defaultValue?: any
  MenuProps?: any
  onOpen?: (event: any) => void
  onClose?: (event: any) => void
  loadOptions?: (
    params: SelectAsyncLoadParams,
  ) => Promise<SelectAsyncLoadResult> | SelectAsyncLoadResult
  pageSize?: number
  clearable?: boolean
}

const containsText = (text: string, searchText: string) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1

const sameValue = (left: any, right: any) => String(left) === String(right)

const mergeUniqueAsyncOptions = (
  existingOptions: Array<SelectAsyncOption>,
  incomingOptions: Array<SelectAsyncOption>,
) => {
  const merged = [...existingOptions]

  incomingOptions.forEach((incomingOption) => {
    const exists = merged.some((option) =>
      sameValue(option.value, incomingOption.value),
    )

    if (!exists) {
      merged.push(incomingOption)
    }
  })

  return merged
}

export function Select({
  control,
  register: _register,
  options,
  name,
  label,
  sx,

  topRow,
  searchable,
  defaultValue,
  MenuProps,
  onOpen,
  onClose,
  loadOptions,
  pageSize = 1,
  clearable = false,
  ...rest
}: SmartSelectProps) {
  void _register
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [asyncOptions, setAsyncOptions] = useState<Array<SelectAsyncOption>>([])
  const [asyncPage, setAsyncPage] = useState(0)
  const [hasMoreAsyncOptions, setHasMoreAsyncOptions] = useState(false)
  const [isLoadingAsyncOptions, setIsLoadingAsyncOptions] = useState(false)
  const asyncRequestIdRef = useRef(0)
  const hasMoreAsyncOptionsRef = useRef(false)
  const isLoadingAsyncOptionsRef = useRef(false)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const isAsyncMode = Boolean(loadOptions)

  useEffect(() => {
    hasMoreAsyncOptionsRef.current = hasMoreAsyncOptions
  }, [hasMoreAsyncOptions])

  useEffect(() => {
    isLoadingAsyncOptionsRef.current = isLoadingAsyncOptions
  }, [isLoadingAsyncOptions])

  useEffect(() => {
    if (!isAsyncMode) return

    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchText(searchText)
    }, 250)

    return () => clearTimeout(debounceTimeout)
  }, [isAsyncMode, searchText])

  useEffect(() => {
    if (!searchable || !isMenuOpen) return

    const timeoutId = window.setTimeout(() => {
      if (
        searchInputRef.current &&
        document.activeElement !== searchInputRef.current
      ) {
        searchInputRef.current.focus()
      }
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [
    asyncOptions.length,
    debouncedSearchText,
    isLoadingAsyncOptions,
    isMenuOpen,
    searchable,
  ])

  const loadAsyncPage = useCallback(
    async (
      page: number,
      mode: 'replace' | 'append',
      searchTerm: string = debouncedSearchText,
    ) => {
      if (!loadOptions) return
      if (
        mode === 'append' &&
        (isLoadingAsyncOptionsRef.current || !hasMoreAsyncOptionsRef.current)
      ) {
        return
      }

      const requestId = ++asyncRequestIdRef.current
      setIsLoadingAsyncOptions(true)

      if (mode === 'replace') {
        setAsyncOptions([])
        setAsyncPage(0)
        setHasMoreAsyncOptions(false)
      }

      try {
        const result = await loadOptions({
          search: searchTerm.trim(),
          page,
          limit: pageSize,
        })

        if (requestId !== asyncRequestIdRef.current) return

        const nextOptions = Array.isArray(result?.options) ? result.options : []
        setAsyncOptions((previousOptions) =>
          mode === 'replace'
            ? mergeUniqueAsyncOptions([], nextOptions)
            : mergeUniqueAsyncOptions(previousOptions, nextOptions),
        )
        setAsyncPage(page)
        setHasMoreAsyncOptions(
          typeof result?.hasMore === 'boolean'
            ? result.hasMore
            : nextOptions.length >= pageSize,
        )
      } catch (error) {
        if (requestId !== asyncRequestIdRef.current) return
        setHasMoreAsyncOptions(false)
        if (mode === 'replace') {
          setAsyncOptions([])
          setAsyncPage(0)
        }
        console.error('Failed to load async select options', error)
      } finally {
        if (requestId === asyncRequestIdRef.current) {
          setIsLoadingAsyncOptions(false)
        }
      }
    },
    [debouncedSearchText, loadOptions, pageSize],
  )

  useEffect(() => {
    if (!isAsyncMode || !isMenuOpen) return

    void loadAsyncPage(1, 'replace', debouncedSearchText)
  }, [debouncedSearchText, isAsyncMode, isMenuOpen, loadAsyncPage])

  const handleAsyncScroll = useCallback(
    (event: any) => {
      if (
        !isAsyncMode ||
        isLoadingAsyncOptionsRef.current ||
        !hasMoreAsyncOptionsRef.current
      ) {
        return
      }

      const target = event?.target
      if (!target) return

      const distanceFromBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight

      if (distanceFromBottom <= 16) {
        void loadAsyncPage(asyncPage + 1, 'append', debouncedSearchText)
      }
    },
    [asyncPage, debouncedSearchText, isAsyncMode, loadAsyncPage],
  )

  return (
    <FormControl sx={sx} disabled={rest.disabled}>
      {label && <InputLabel id={`${name}-select-label`}>{label}</InputLabel>}
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue ?? ''}
        render={({ field: { ...fieldProps }, fieldState: { error } }) => (
          <>
            {(() => {
              const localOptions = options
                ? Object.entries(options)
                    .filter(
                      ([value, optionLabel]) =>
                        containsText(optionLabel.toString(), searchText) ||
                        sameValue(fieldProps.value, value),
                    )
                    .sort(([, labelA], [, labelB]) =>
                      labelA.toString().localeCompare(labelB.toString()),
                    )
                    .map(([value, optionLabel]) => ({
                      value,
                      label: optionLabel,
                    }))
                : []

              const remoteOptions = [...asyncOptions]

              if (
                isAsyncMode &&
                fieldProps.value !== undefined &&
                fieldProps.value !== null &&
                fieldProps.value !== '' &&
                !remoteOptions.some((option) =>
                  sameValue(option.value, fieldProps.value),
                )
              ) {
                remoteOptions.unshift({
                  value: fieldProps.value,
                  label: fieldProps.value,
                })
              }

              const renderedOptions = isAsyncMode ? remoteOptions : localOptions

              const mergedMenuProps = {
                ...MenuProps,
                ...(searchable
                  ? {
                      autoFocus: false,
                      disableAutoFocusItem: true,
                    }
                  : {}),
                MenuListProps: {
                  ...MenuProps?.MenuListProps,
                  ...(searchable ? { autoFocusItem: false } : {}),
                  onScroll: (event: any) => {
                    MenuProps?.MenuListProps?.onScroll?.(event)
                    handleAsyncScroll(event)
                  },
                },
                PaperProps: {
                  ...MenuProps?.PaperProps,
                  onScroll: (event: any) => {
                    MenuProps?.PaperProps?.onScroll?.(event)
                    handleAsyncScroll(event)
                  },
                },
              }

              return (
                <MuiSelect
                  labelId={`${name}-select-label`}
                  id={`${name}-select`}
                  label={label}
                  error={Boolean(error?.message)}
                  sx={
                    clearable
                      ? {
                          '& .MuiSelect-select': {
                            paddingRight: '92px !important',
                          },
                        }
                      : undefined
                  }
                  {...rest}
                  {...fieldProps}
                  MenuProps={mergedMenuProps}
                  input={
                    clearable ? (
                      <OutlinedInput
                        label={label}
                        endAdornment={
                          fieldProps.value !== undefined &&
                          fieldProps.value !== null &&
                          fieldProps.value !== '' ? (
                            <InputAdornment position="end" sx={{ mr: 2.5 }}>
                              <IconButton
                                size="small"
                                tabIndex={-1}
                                onMouseDown={(event) => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                }}
                                onClick={(event) => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  fieldProps.onChange('')
                                }}
                              >
                                <Close />
                              </IconButton>
                            </InputAdornment>
                          ) : undefined
                        }
                      />
                    ) : undefined
                  }
                  onOpen={(event) => {
                    onOpen?.(event)
                    setIsMenuOpen(true)
                  }}
                  onClose={(event) => {
                    onClose?.(event)
                    setIsMenuOpen(false)
                  }}
                >
                  {searchable && (
                    <ListSubheader>
                      <TextField
                        size="small"
                        placeholder="Type to search..."
                        fullWidth
                        inputRef={searchInputRef}
                        value={searchText}
                        autoComplete="off"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Escape') {
                            e.stopPropagation()
                          }
                        }}
                      />
                    </ListSubheader>
                  )}
                  {topRow && (
                    <MenuItem key={topRow.value} value={topRow.value}>
                      {topRow.label}
                    </MenuItem>
                  )}
                  {renderedOptions.map((option) => (
                    <MenuItem key={String(option.value)} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                  {isAsyncMode && isLoadingAsyncOptions && (
                    <MenuItem disabled value="__loading__">
                      {asyncPage > 0 ? 'Loading more...' : 'Loading...'}
                    </MenuItem>
                  )}
                  {isAsyncMode &&
                    !isLoadingAsyncOptions &&
                    renderedOptions.length === 0 && (
                      <MenuItem disabled value="__no-results__">
                        No results found
                      </MenuItem>
                    )}
                </MuiSelect>
              )
            })()}
            {error?.message && (
              <FormHelperText error>{error.message}</FormHelperText>
            )}
          </>
        )}
      />
    </FormControl>
  )
}
